// src/modules/app-views/customers/pages/Document/tabs/AdditionalInfoTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card, Descriptions, Empty, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import translateX from 'utils/translateX';
import { formatCurrency } from 'utils/numberFormat';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const EntityAdditionalInfoTab = () => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { data, loading } = useDocumentContext();

  // ---------------------------------------------------------------------------
  // 2. LOCAL VARIABLES
  // ---------------------------------------------------------------------------
  // Check if entity has enrichment data
  const hasEnrichmentData =
    data?.dataEnrichment?.cnpj?.isEnriched || data?.dataEnrichment?.cpf?.isEnriched;

  // const hasEnrichmentData = useMemo(() => {
  //   return !!(
  //     data?.foundedAt ||
  //     data?.mainActivity ||
  //     data?.additionalActivities?.length > 0 ||
  //     data?.holders?.length > 0 ||
  //     data?.legalNature ||
  //     data?.shareCapital ||
  //     data?.companySize ||
  //     data?.registrationStatus
  //   );
  // }, [data]);

  // Format registration status with color
  const registrationStatusTag = useMemo(() => {
    if (!data?.registrationStatus) return '-';

    // Handle both string and object formats
    // Object format: { code: "2", description: "Ativa", date: "2009-02-13" }
    // Normalized format uses 'code' instead of 'status' and 'description' instead of 'statusText'
    const statusCode =
      typeof data.registrationStatus === 'string'
        ? data.registrationStatus
        : data.registrationStatus?.code || data.registrationStatus?.status;

    const statusDescription =
      typeof data.registrationStatus === 'string'
        ? data.registrationStatus
        : data.registrationStatus?.description || data.registrationStatus?.statusText || '-';

    // Map by code (normalized) or by description text
    const statusMap = {
      // By code
      '2': { color: 'success', label: statusDescription },
      '3': { color: 'warning', label: statusDescription },
      '4': { color: 'warning', label: statusDescription },
      '8': { color: 'error', label: statusDescription },
      // By description text (fallback)
      ATIVA: { color: 'success', label: 'Ativa' },
      BAIXADA: { color: 'error', label: 'Baixada' },
      INAPTA: { color: 'warning', label: 'Inapta' },
      SUSPENSA: { color: 'warning', label: 'Suspensa' },
      NULA: { color: 'error', label: 'Nula' },
    };

    const status = statusMap[statusCode] ||
      statusMap[statusDescription?.toUpperCase()] || {
        color: 'default',
        label: statusDescription,
      };

    return <Tag color={status.color}>{status.label}</Tag>;
  }, [data?.registrationStatus]);

  // Format main activity
  // Object format: { code: "6201501", description: "Desenvolvimento...", isPrimary: true }
  const mainActivityText = useMemo(() => {
    if (!data?.mainActivity) return '-';
    const { code, description } = data.mainActivity;
    if (code && description) return `${code} - ${description}`;
    return description || code || '-';
  }, [data?.mainActivity]);

  // Format additional activities
  // Array of objects: { code: "4751201", description: "Comércio...", isPrimary: false }
  const additionalActivitiesText = useMemo(() => {
    if (!data?.additionalActivities?.length) return '-';
    return data.additionalActivities.map((activity, index) => {
      const { code, description } = activity;
      const label = code && description ? `${code} - ${description}` : description || code;
      return (
        <div key={index} style={{ marginBottom: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {label}
          </Text>
        </div>
      );
    });
  }, [data?.additionalActivities]);

  // Format holders (QSA - Quadro Societário)
  const holdersContent = useMemo(() => {
    if (!data?.holders?.length) return '-';
    return data.holders.map((holder, index) => (
      <div key={index} style={{ marginBottom: 8 }}>
        <Text strong>{holder.name}</Text>
        {holder.qualification && (
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            {holder.qualification}
          </Text>
        )}
      </div>
    ));
  }, [data?.holders]);

  // Format share capital
  const shareCapitalText = useMemo(() => {
    if (!data?.shareCapital) return '-';
    return formatCurrency(data.shareCapital);
  }, [data?.shareCapital]);

  // Format founded date
  const foundedAtText = useMemo(() => {
    if (!data?.foundedAt) return '-';
    return dayjs(data.foundedAt).format('DD/MM/YYYY');
  }, [data?.foundedAt]);

  // Check if entity is a company (only companies have this data)
  const isCompany = data?.entityType === 'company';

  // ---------------------------------------------------------------------------
  // 3. UI
  // ---------------------------------------------------------------------------
  // Show message for non-company entities
  if (!isCompany && !loading) {
    return (
      <div className="container" style={{ position: 'relative', minHeight: 120 }}>
        <Card className="mt-3">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={translateX('additional_info_only_for_companies')}
          />
        </Card>
      </div>
    );
  }

  // Show message when no enrichment data is available
  if (!hasEnrichmentData && !loading) {
    return (
      <div className="container" style={{ position: 'relative', minHeight: 120 }}>
        <Card className="mt-3">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">{translateX('no_enrichment_data_available')}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {translateX('no_enrichment_data_hint')}
                </Text>
              </div>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="additionalInfo" isFetching={loading} data={data} />

      {/* Company Registration Data */}
      <Card className="mt-3" title={translateX('company_registration_data')}>
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label={translateX('registration_status')}>
            {registrationStatusTag}
          </Descriptions.Item>

          <Descriptions.Item label={translateX('founded_at')}>{foundedAtText}</Descriptions.Item>

          <Descriptions.Item label={translateX('legal_nature')} span={2}>
            {data?.legalNature
              ? typeof data.legalNature === 'object'
                ? `${data.legalNature.code || ''} - ${data.legalNature.description || ''}`.replace(/^- | -$/g, '')
                : data.legalNature
              : '-'}
          </Descriptions.Item>

          <Descriptions.Item label={translateX('share_capital')}>
            {shareCapitalText}
          </Descriptions.Item>

          <Descriptions.Item label={translateX('company_size')}>
            {data?.companySize
              ? typeof data.companySize === 'object'
                ? data.companySize.description || data.companySize.code || '-'
                : data.companySize
              : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Economic Activities */}
      <Card className="mt-3" title={translateX('economic_activities')}>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label={translateX('main_activity')}>
            {mainActivityText}
          </Descriptions.Item>

          <Descriptions.Item label={translateX('additional_activities')}>
            {additionalActivitiesText}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Partners / Holders (QSA) */}
      {data?.holders?.length > 0 && (
        <Card className="mt-3" title={translateX('company_holders')}>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={translateX('partners_list')}>
              {holdersContent}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default EntityAdditionalInfoTab;
